import { makeAutoObservable, runInAction } from 'mobx';
import { api } from 'api/axios';
import { MedicalcareDoctor, Pharmacy } from 'model/medicare';
import {
  BusinessHours,
  DayAvailability,
  HourAvailability,
  SpecialAvailability,
} from 'views/Medical/types';
import { Role } from 'model/account';
import { queryClient } from 'utils/reactQueryClient';

class MedicalCareStore {
  doctor: MedicalcareDoctor | null = null;

  pharmacy: Pharmacy | null = null;

  isLoading = false;

  isDoctor = true;

  constructor() {
    makeAutoObservable(this);
  }

  private getBusinessHours(): BusinessHours | undefined {
    return this.isDoctor
      ? this.doctor?.businessHours
      : this.pharmacy?.businessHours;
  }

  private async invalidateBusinessHoursQueries() {
    if (this.isDoctor && this.doctor?.userId) {
      await Promise.all([
        queryClient.invalidateQueries(['doctor', this.doctor.userId]),
        queryClient.invalidateQueries(['doctorSchedule', this.doctor.userId]),
      ]);
      return;
    }
    if (!this.isDoctor && this.pharmacy?.userId) {
      await Promise.all([
        queryClient.invalidateQueries(['pharmacy', this.pharmacy.userId]),
        queryClient.invalidateQueries(['pharmacySchedule', this.pharmacy.userId]),
      ]);
    }
  }

  async hydrate(userId: string, role: Role) {
    this.isDoctor = role === Role.DOCTOR;
    runInAction(() => {
      this.isLoading = true;
    });
    try {
      if (role === Role.DOCTOR) {
        const { data } = await api.get<MedicalcareDoctor>(
          `/medicalcare/doctors/${userId}`,
        );
        runInAction(() => {
          this.doctor = data;
          this.pharmacy = null;
        });
      } else if (role === Role.PHARMACIST) {
        const { data } = await api.get<Pharmacy>(
          `/medicalcare/pharmacies/${userId}`,
        );
        runInAction(() => {
          this.pharmacy = data;
          this.doctor = null;
        });
      }
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private async persistBusinessHours(next: BusinessHours) {
    if (this.isDoctor && this.doctor) {
      await api.put(`/medicalcare/doctors/${this.doctor.userId}`, {
        businessHours: next,
      });
      runInAction(() => {
        this.doctor = {
          ...this.doctor!,
          businessHours: next as MedicalcareDoctor['businessHours'],
        };
      });
      await this.invalidateBusinessHoursQueries();
      return;
    }
    if (!this.isDoctor && this.pharmacy) {
      await api.put(`/medicalcare/pharmacies/${this.pharmacy.userId}`, {
        businessHours: next,
      });
      runInAction(() => {
        this.pharmacy = {
          ...this.pharmacy!,
          businessHours: next as Pharmacy['businessHours'],
        };
      });
      await this.invalidateBusinessHoursQueries();
    }
  }

  async updateWeekly(weekly: DayAvailability[], holidayHours?: HourAvailability[]) {
    const base = this.getBusinessHours();
    if (!base) return;
    runInAction(() => {
      this.isLoading = true;
    });
    try {
      await this.persistBusinessHours({
        ...base,
        weekly,
        ...(holidayHours
          ? {
              holidays: {
                hours: holidayHours,
              },
            }
          : {}),
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateSpecialDay(specialDays: SpecialAvailability[]) {
    const base = this.getBusinessHours();
    if (!base) return;
    runInAction(() => {
      this.isLoading = true;
    });
    try {
      await this.persistBusinessHours({ ...base, specialDays });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async deleteSpecialDay(day: SpecialAvailability) {
    const base = this.getBusinessHours();
    if (!base?.specialDays) return;
    const specialDays = base.specialDays.filter(
      (s) =>
        !(s.year === day.year && s.month === day.month && s.day === day.day),
    );
    runInAction(() => {
      this.isLoading = true;
    });
    try {
      await this.persistBusinessHours({ ...base, specialDays });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }
}

export default new MedicalCareStore();
